import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import cn from '../../utils/cn.ts';

export enum BadgeVariant {
  PRIMARY,
  DESTRUCTIVE,
  CAUTION,
  WARNING,
  SUCCESS,
  INFO,
  BLOCKED,
  DEFAULT,
  SKY,
  BRAND,
  ROSE,
  VIOLET,
  ORANGE,
  YELLOW,
  GREY,
  DISABLED,
  OUTLINE,
  SECONDARY,
  GREEN,
  RED,
}

export enum BadgeSize {
  DEFAULT,
  SMALL,
  LARGE,
  XLARGE,
}

const badgeVariants = cva('inline-flex items-center justify-center gap-1.5 rounded-md', {
  variants: {
    variant: {
      [BadgeVariant.DESTRUCTIVE]: 'bg-red-100 text-red-500',
      [BadgeVariant.CAUTION]: 'bg-yellow-100 text-yellow-500',
      [BadgeVariant.WARNING]: 'bg-orange-100 text-orange-500',
      [BadgeVariant.SUCCESS]: 'bg-green-100 text-green-500',
      [BadgeVariant.INFO]: 'bg-blue-100 text-blue-500',
      [BadgeVariant.BLOCKED]: 'bg-neutral-100 text-neutral-400',
      [BadgeVariant.DEFAULT]: 'bg-neutral-100 text-neutral-500',
      [BadgeVariant.SKY]: 'bg-sky-500 text-white',
      [BadgeVariant.BRAND]: 'bg-brand text-white',
      [BadgeVariant.ROSE]: 'bg-rose-500 text-white',
      [BadgeVariant.VIOLET]: 'bg-violet-500 text-white',
      [BadgeVariant.ORANGE]: 'bg-orange-500 text-white',
      [BadgeVariant.YELLOW]: 'bg-yellow-500 text-white',
      [BadgeVariant.GREY]: 'bg-neutral-500 text-white',
      [BadgeVariant.DISABLED]: 'bg-neutral-400 text-white',
      [BadgeVariant.PRIMARY]: 'bg-primary text-white',
      [BadgeVariant.OUTLINE]: 'border border-neutral-300 bg-white',
      [BadgeVariant.SECONDARY]:
        'border border-neutral-200 bg-neutral-100 text-neutral-500',
      [BadgeVariant.GREEN]: 'bg-green-500 text-white',
      [BadgeVariant.RED]: 'bg-red-500 text-white',
    } satisfies Record<BadgeVariant, string>,
    size: {
      [BadgeSize.DEFAULT]: 'px-2 py-1.5 text-xs',
      [BadgeSize.SMALL]: 'px-1.5 py-1 text-2xs',
      [BadgeSize.LARGE]: 'px-2 py-1.5 text-sm',
      [BadgeSize.XLARGE]: 'p-2 [&>svg]:size-4 gap-2.5 text-sm',
    } satisfies Record<BadgeSize, string>,
  },
  defaultVariants: {
    variant: BadgeVariant.DEFAULT,
    size: BadgeSize.DEFAULT,
  },
});

export type BadgeProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof badgeVariants>;

const Badge = React.memo(
  React.forwardRef<HTMLDivElement, BadgeProps>(
    (
      { className, variant = BadgeVariant.DEFAULT, size = BadgeSize.DEFAULT, ...props },
      ref,
    ) => {
      return (
        <div
          className={cn(badgeVariants({ variant, size }), className)}
          ref={ref}
          {...props}
        />
      );
    },
  ),
);

Badge.displayName = 'Badge';

export default Badge;
